import { memo, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { AlertCircle, RotateCw } from 'lucide-react';
import type { UIMessage } from '@/types/chat';
import { ToolCallCard } from './ToolCallCard';
import { DiffPreview } from './DiffPreview';
import { ClarificationCard } from './ClarificationCard';
import { FeedbackActions } from './FeedbackActions';
import { SuggestionChips } from './SuggestionChips';
import { FormulaPill } from './FormulaPill';
import { useChatStore } from '@/stores/chatStore';
import { shouldHideAssistantBubble, stripBoilerplate, linkifyCitations } from '@/utils/cleanText';
import { selectRange } from '@/services/office';

interface Props {
  m: UIMessage;
  conversationId: string | null;
  isLast?: boolean;
  // Carries the message id so the parent can pass ONE stable callback for
  // the whole list instead of a per-bubble closure (which would defeat
  // the React.memo below).
  onClarifyPick?: (messageId: string, interpretation: string) => void;
  onSuggestionPick?: (text: string) => void;
}

// Module scope so the plugin array + component map keep a stable identity
// — inline literals would force ReactMarkdown to treat every render as a
// prop change, defeating the markdown memoization below.
const remarkPlugins = [remarkGfm];

const markdownComponents = {
  code({ inline, className, children, ...props }: React.HTMLAttributes<HTMLElement> & { inline?: boolean }) {
    // Day 3: inline Excel formulas get the FormulaPill
    // treatment — hover for arg breakdown. Non-formula
    // code spans keep their plain-mono styling.
    const text = String(children ?? '').trim();
    if (inline && text.startsWith('=') && text.length > 3) {
      return <FormulaPill formula={text} />;
    }
    return inline ? (
      <code
        className="rounded bg-black/10 px-1 py-0.5 font-mono text-xs dark:bg-white/10"
        {...props}
      >
        {children}
      </code>
    ) : (
      <pre className="my-2 overflow-x-auto rounded-md bg-black/5 p-2 font-mono text-xs dark:bg-white/10">
        <code className={className}>{children}</code>
      </pre>
    );
  },
  a({ href, children, ...props }: React.AnchorHTMLAttributes<HTMLAnchorElement>) {
    // Cell citations use a `cell:` scheme — clicking jumps
    // to the cited range in the live workbook instead of
    // navigating the browser.
    if (href?.startsWith('cell:')) {
      const [rawSheet, rawAddr] = href.slice('cell:'.length).split('|');
      const sheet = decodeURIComponent(rawSheet ?? '');
      const addr = decodeURIComponent(rawAddr ?? '');
      return (
        <button
          type="button"
          onClick={() => void selectRange(sheet, addr)}
          className="rounded bg-primary/10 px-1 font-mono text-xs text-primary hover:bg-primary/20"
          title={`Go to ${sheet}!${addr}`}
        >
          {children}
        </button>
      );
    }
    return (
      <a href={href} target="_blank" rel="noreferrer" className="text-primary underline" {...props}>
        {children}
      </a>
    );
  },
};

// memo(): during SSE streaming only the trailing assistant message object
// changes identity in the store — every other bubble's props are stable,
// so the whole transcript above the stream no longer re-renders (and
// re-parses its markdown) on each token delta.
export const MessageBubble = memo(function MessageBubble({ m, conversationId, isLast, onClarifyPick, onSuggestionPick }: Props) {
  const isUser = m.role === 'user';
  // Narrow selector — actions are stable, so this never re-renders the
  // bubble (a whole-store subscription here would fire on every token).
  const dismissClarify = useChatStore((s) => s.dismissClarify);

  const hasTools = !isUser && (m.tools?.length ?? 0) > 0;
  const hasDiff = !isUser && m.diff && m.diff.changes.length > 0;
  const hasClarify = !isUser && m.clarify;

  // Strip LLM boilerplate from assistant messages (preambles like "I'll
  // sort the data..." and postambles like "Done — 10 rows added"). The
  // diff card already shows what changed; repeating it in prose is pure
  // attention tax. User messages stay verbatim.
  const displayContent = useMemo(
    () => (isUser ? m.content : linkifyCitations(stripBoilerplate(m.content))),
    [isUser, m.content],
  );
  // Hide the bubble entirely when:
  //   (a) cleaned text is empty AND diff/tool cards carry the meaning, OR
  //   (b) streaming AND no content yet (LoadingPulse already shows progress —
  //       rendering an empty "…" bubble alongside it = double indicator).
  const hideEmptyBubble =
    !isUser
    && !m.error
    && (
      (m.streaming && !displayContent)
      || (!m.streaming && shouldHideAssistantBubble(displayContent, hasDiff || hasTools))
    );

  // WRITE-turn minimalism, frontend-side: when the message carries a diff
  // (the card is the real receipt) and the prose is long, collapse it to
  // its first line with a "Show more" toggle. Nothing is lost — the full
  // explanation ("here's why I chose these fields…") is one tap away.
  // (An earlier server-side chop destroyed requested explanations.)
  const [expanded, setExpanded] = useState(false);
  const collapsible = !isUser && !m.streaming && !!hasDiff && displayContent.length > 240;
  const visibleContent = useMemo(() => {
    if (!collapsible || expanded) return displayContent;
    const firstLine = displayContent.split('\n').find((l) => l.trim() !== '') ?? displayContent;
    return firstLine.length > 240 ? firstLine.slice(0, 240) + '…' : firstLine;
  }, [collapsible, expanded, displayContent]);

  // Markdown parsing is the priciest part of a bubble render — memoize
  // the element keyed on the message identity + the fields the render
  // actually uses, so only the streaming bubble re-parses per delta.
  const markdownBlock = useMemo(
    () => (
      <div className="prose-sm max-w-none">
        <ReactMarkdown
          remarkPlugins={remarkPlugins}
          components={markdownComponents}
        >
          {visibleContent || (m.streaming ? '…' : '')}
        </ReactMarkdown>
        {collapsible && (
          <button
            type="button"
            onClick={() => setExpanded((e) => !e)}
            className="mt-1 text-xs font-medium text-primary hover:underline"
          >
            {expanded ? 'Show less' : 'Show more'}
          </button>
        )}
      </div>
    ),
    [m.id, visibleContent, m.streaming, collapsible, expanded],
  );

  return (
    <div className={['flex flex-col gap-1.5', isUser ? 'items-end' : 'items-start'].join(' ')}>
      {hasTools && (
        <div className="w-full max-w-[90%] space-y-1">
          {m.tools!.map((t) => (
            <ToolCallCard key={`${t.id}-${t.iteration}`} tool={t} />
          ))}
        </div>
      )}

      {hasClarify && (
        <ClarificationCard
          messageId={m.id}
          state={m.clarify!}
          onPick={(interp) => onClarifyPick?.(m.id, interp)}
          onDismiss={() => dismissClarify(m.id)}
          disabled={m.clarify!.status !== 'pending'}
        />
      )}

      {hasDiff && <DiffPreview conversationId={conversationId} message={m} />}

      {(displayContent || m.streaming || m.error) && !hideEmptyBubble && (
        <div
          className={[
            'max-w-[90%] rounded-lg px-3 py-2 text-sm leading-relaxed',
            isUser
              ? 'bg-primary text-white'
              : 'bg-surface text-text-primary dark:bg-surface-dark dark:text-text-primary-dark',
          ].join(' ')}
        >
          {m.error ? (
            <div className="flex flex-col gap-2">
              <div className="flex items-start gap-2 text-red-500">
                <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>{m.error}</span>
              </div>
              {isLast && (
                <button
                  type="button"
                  onClick={() => useChatStore.getState().requestRetry()}
                  className="flex w-fit items-center gap-1.5 rounded-md border border-red-300 px-2 py-1 text-xs text-red-500 hover:bg-red-50 dark:hover:bg-red-950"
                >
                  <RotateCw className="h-3 w-3" />
                  Retry
                </button>
              )}
            </div>
          ) : isUser ? (
            <p className="whitespace-pre-wrap">{m.content}</p>
          ) : (
            markdownBlock
          )}

          {m.usage && !m.streaming && (() => {
            const total = m.usage.tokens_input + m.usage.tokens_output;
            const display = total >= 1000 ? `${(total / 1000).toFixed(1)}K` : `${total}`;
            return (
              <div
                className="mt-1 text-[10px] text-black/40 dark:text-white/40"
                title={`${total.toLocaleString()} tokens this turn (input + output)`}
              >
                {display}
              </div>
            );
          })()}
        </div>
      )}

      {!isUser && !m.streaming && !m.error && m.content && (
        <div className="relative">
          <FeedbackActions
            conversationId={conversationId}
            messageId={m.id}
            aiOutput={m.content}
          />
        </div>
      )}

      {/* Proactive follow-up chips — last assistant turn only. Older
          turns keep their chips in state but we don't re-render them,
          otherwise the thread would fill with stale suggestions. */}
      {!isUser && isLast && !m.streaming && !m.error && (m.suggestions?.length ?? 0) > 0 && (
        <SuggestionChips
          suggestions={m.suggestions!}
          onPick={(text) => onSuggestionPick?.(text)}
        />
      )}
    </div>
  );
});
